export type GoogleOneTapCredentialResponse = {
  clientId?: string;
  credential?: string;
  select_by?: string;
};

type GoogleOneTapCredentialHandler = (
  response: GoogleOneTapCredentialResponse
) => void;

type GoogleAccountsId = {
  initialize: (options: {
    auto_select?: boolean;
    callback: (response: GoogleOneTapCredentialResponse) => void;
    cancel_on_tap_outside?: boolean;
    client_id: string;
    context?: "signin" | "signup" | "use";
    use_fedcm_for_prompt?: boolean;
  }) => void;
  prompt: () => void;
};

type GoogleOneTapWindow = Window & {
  google?: {
    accounts?: {
      id?: GoogleAccountsId;
    };
  };
};

type GoogleOneTapClient = {
  initialize: (params: {
    clientId: string;
    onCredential: GoogleOneTapCredentialHandler;
  }) => boolean;
  prompt: () => void;
};

const GOOGLE_ONE_TAP_SCRIPT_READY_EVENT = "google-one-tap-script-ready";
let isGoogleOneTapScriptReady = false;

function getGoogleAccountsId(
  targetWindow: GoogleOneTapWindow
): GoogleAccountsId | null {
  return targetWindow.google?.accounts?.id ?? null;
}

export function createGoogleOneTapClient(
  targetWindow: GoogleOneTapWindow
): GoogleOneTapClient {
  let hasInitialized = false;
  let credentialHandler: GoogleOneTapCredentialHandler | null = null;

  return {
    initialize({ clientId, onCredential }) {
      credentialHandler = onCredential;

      const googleAccountsId = getGoogleAccountsId(targetWindow);
      if (!googleAccountsId) {
        return false;
      }

      if (hasInitialized) {
        return true;
      }

      googleAccountsId.initialize({
        auto_select: false,
        callback: (response) => credentialHandler?.(response),
        cancel_on_tap_outside: true,
        client_id: clientId,
        context: "signin",
        use_fedcm_for_prompt: false,
      });
      hasInitialized = true;
      return true;
    },
    prompt() {
      const googleAccountsId = getGoogleAccountsId(targetWindow);
      if (!(hasInitialized && googleAccountsId)) {
        return;
      }

      googleAccountsId.prompt();
    },
  };
}

let googleOneTapClient: GoogleOneTapClient | null = null;

export function getGoogleOneTapClient(): GoogleOneTapClient | null {
  if (typeof window === "undefined") {
    return null;
  }

  googleOneTapClient ??= createGoogleOneTapClient(window as GoogleOneTapWindow);
  return googleOneTapClient;
}

export function subscribeGoogleOneTapScriptReady(onStoreChange: () => void) {
  window.addEventListener(GOOGLE_ONE_TAP_SCRIPT_READY_EVENT, onStoreChange);

  return () => {
    window.removeEventListener(
      GOOGLE_ONE_TAP_SCRIPT_READY_EVENT,
      onStoreChange
    );
  };
}

export function getGoogleOneTapScriptReadySnapshot() {
  return isGoogleOneTapScriptReady;
}

export function getGoogleOneTapScriptReadyServerSnapshot() {
  return false;
}

export function markGoogleOneTapScriptReady() {
  if (isGoogleOneTapScriptReady) {
    return;
  }

  isGoogleOneTapScriptReady = true;
  window.dispatchEvent(new Event(GOOGLE_ONE_TAP_SCRIPT_READY_EVENT));
}
